import { searchCompanies, searchCompanyDnB, searchCompanyDnBWithRegistration } from "../api/companyApi";
import countries from "../utils/countries";

// The shared API interpolates this value into a path. Encode only in the default
// transport adapter so injected search implementations continue to receive data.
const registryRegistrationSearch = (params) => searchCompanyDnBWithRegistration({
  ...params, registrationNumber: encodeURIComponent(params.registrationNumber),
});

const text = (value) => (typeof value === "string" || typeof value === "number") ? String(value).trim() : "";
const words = (value) => text(value).normalize("NFKC").toLocaleLowerCase("en").replace(/[^\p{L}\p{N}]+/gu, " ").trim();
const identifier = (value) => text(value).normalize("NFKC").toUpperCase().replace(/[\s.-]/g, "");
const duns = (value) => {
  const valueText = text(value).replace(/[\s-]/g, "");
  return /^\d{9}$/.test(valueText) ? valueText : "";
};
const countryCodes = new Map();
Object.entries(countries).forEach(([code, names]) => {
  if (!/^[A-Z]{2}$/.test(code) || code === "UK") return;
  countryCodes.set(words(code), code);
  names.forEach((name) => countryCodes.set(words(name), code));
});
countryCodes.set("uk", "GB");
countryCodes.set("usa", "US");
const countryCode = (value) => countryCodes.get(words(value)) || "";
const companyNameTokens = (value) => new Set(words(value).split(" ").filter((token) => token.length > 1 && !/^(the|and|inc|ltd|llc|limited|corp|corporation|company|plc)$/.test(token)));
const overlaps = (left, right) => [...companyNameTokens(left)].some((part) => companyNameTokens(right).has(part));
const addressText = (value) => typeof value === "string" ? value : [
  value?.streetAddress?.line1, value?.streetAddress?.line2, value?.streetAddressLine1,
  value?.streetAddressLine2, value?.addressLocality?.name, value?.addressRegion?.name,
  value?.postalCode,
].filter(Boolean).join(", ");
const registrationNumbers = (row) => [...new Set([
  row.registrationNumber, row.registration_number, row.primaryRegistrationNumber,
  ...(Array.isArray(row.registrationNumbers) ? row.registrationNumbers.map((item) => typeof item === "object" ? item?.registrationNumber || item?.number : item) : []),
].map(text).filter(Boolean))];

function entityIdentity(row = {}) {
  const address = row.primaryAddress || row.address;
  const country = text(row.country || row.countryName || row.primaryAddress?.addressCountry?.name);
  const iso2Code = countryCode(row.iso2Code || row.countryCode || row.primaryAddress?.addressCountry?.isoAlpha2Code) || countryCode(country);
  const registrations = registrationNumbers(row);
  return {
    name: text(row.name || row.legalName || row.companyName || row.primaryName),
    country: country || (iso2Code ? countries[iso2Code]?.[0] || iso2Code : ""),
    iso2Code,
    registrationNumber: registrations[0] || "",
    registrationNumbers: registrations,
    dunsNumber: duns(row.dunsNumber || row.duns_number || row.dnbCompanyId || row.duns),
    address: addressText(address) || [row.city, row.state, row.postalCode || row.postal_code].map(text).filter(Boolean).join(", "),
    website: text(row.website || row.domain),
  };
}

function sameRegistration(left, right) {
  return Boolean(left.iso2Code && left.iso2Code === right.iso2Code && left.registrationNumbers.some((value) => right.registrationNumbers.some((other) => identifier(value) === identifier(other))));
}

// This is identity evidence, not a statistical confidence or a credit assessment.
function compareIdentity(input, candidate) {
  const reasons = [], conflicts = [];
  const sameDuns = Boolean(input.dunsNumber && input.dunsNumber === candidate.dunsNumber);
  const sameReg = sameRegistration(input, candidate);
  const sameName = Boolean(input.name && words(input.name) === words(candidate.name));
  const relatedName = sameName || overlaps(input.name, candidate.name);
  const sameCountry = Boolean(input.iso2Code && input.iso2Code === candidate.iso2Code);
  const sameAddress = Boolean(input.address && candidate.address && words(input.address) === words(candidate.address));
  if (sameDuns) reasons.push("DUNS number matches exactly");
  if (sameReg) reasons.push("Registration number and country match");
  if (sameName) reasons.push("Legal name matches, ignoring punctuation and case");
  else if (relatedName) reasons.push("Legal names share words; review the complete legal name");
  if (sameCountry) reasons.push("Country matches");
  if (sameAddress) reasons.push("Address matches, ignoring punctuation and case");
  if (input.dunsNumber && candidate.dunsNumber && !sameDuns) conflicts.push("DUNS numbers differ");
  if (input.iso2Code && candidate.iso2Code && !sameCountry) conflicts.push("Countries differ");
  if (sameCountry && input.registrationNumbers.length && candidate.registrationNumbers.length && !sameReg) conflicts.push("Registration numbers differ");
  const evidenceLevel = sameDuns || sameReg ? "identifier" : relatedName && (sameCountry || sameAddress) ? "corroborated" : relatedName ? "name_only" : "unrelated";
  return { reasons, conflicts, evidenceLevel };
}

function responseError(response) {
  if (response instanceof Error || response?.isAxiosError || response?.response?.status >= 400 || response?.success === false) return response;
  return null;
}

function searchWarning(source, error) {
  const message = text(error?.response?.data?.message || error?.response?.data?.error || error?.message || error?.error);
  const status = error?.response?.status || error?.status;
  if (source === "dnb" && /not enabled|disabled|not configured/i.test(message)) return { source, code: "registry_disabled", message: "D&B search is not enabled for this tenant. Existing prospect results are shown separately." };
  if (status === 401 || status === 403) return { source, code: "access_denied", message: `${source === "existing" ? "Existing prospect" : "D&B"} search is not authorized for this account. No access settings were changed.` };
  if (status === 429) return { source, code: "rate_limited", message: `${source === "existing" ? "Existing prospect" : "D&B"} search reached its request limit. Retry later.` };
  return { source, code: "search_unavailable", message: `${source === "existing" ? "Existing prospect" : "D&B"} search did not complete. Retry before relying on these results.` };
}

function responseRows(response, source) {
  const payload = response?.success !== undefined ? response.data : response;
  if (Array.isArray(payload)) return payload;
  const rows = source === "existing" ? payload?.companies : payload?.searchCandidates || payload?.matchCandidates;
  if (!Array.isArray(rows)) throw new Error("Search returned an unrecognized response");
  return rows;
}

function checkAborted(signal) {
  if (signal?.aborted) {
    const error = new Error("Entity search cancelled");
    error.name = "AbortError";
    throw error;
  }
}

/**
 * Call only after the user requests a candidate search. This function never selects,
 * creates or updates a company. Server-side tenant authorization and transactional
 * duplicate checks are still required when an explicit Match action is submitted.
 */
export async function searchInsuranceEntityCandidates(entity, {
  tenantId, searchExisting = searchCompanies, searchByName = searchCompanyDnB,
  searchByRegistration = registryRegistrationSearch, includeRegistry = false, signal,
} = {}) {
  const scope = text(tenantId);
  if (!scope) throw new Error("A tenant is required for entity search");
  if (entity?.tenantId && text(entity.tenantId) !== scope) throw new Error("The reviewed entity belongs to a different tenant");
  const input = entityIdentity(entity);
  if (input.name.length > 256) throw new Error("Legal name must be 256 characters or fewer before searching");
  if (input.registrationNumbers.some((value) => value.length > 200)) throw new Error("Registration number must be 200 characters or fewer before searching");
  // The legacy backend also interpolates registration into its upstream query.
  // Until that shared transport uses structured parameters, reject delimiters
  // rather than allowing document text to alter an external search request.
  if (input.registrationNumbers.some((value) => /[?&#%\u0000-\u001f]/.test(value))) throw new Error("Registration number contains unsupported query characters. Correct it, or clear it to search by legal name.");
  if (input.country.length > 200) throw new Error("Country must be 200 characters or fewer before searching");
  if (!input.name && !input.dunsNumber && !(input.registrationNumber && input.iso2Code)) throw new Error("Enter a legal name or a valid identifier before searching");
  checkAborted(signal);
  const warnings = [];
  let existingSearchComplete = false;
  const tasks = [Promise.resolve().then(() => searchExisting(scope))];
  const registryQuery = includeRegistry === true && (input.name || (input.registrationNumber && input.iso2Code));
  if (registryQuery) tasks.push(Promise.resolve().then(() => input.registrationNumber && input.iso2Code
    ? searchByRegistration({ tenantId: scope, registrationNumber: input.registrationNumber, iso2Code: input.iso2Code })
    : searchByName({ tenantId: scope, name: input.name, iso2Code: input.iso2Code, countryName: input.country })));
  else if (includeRegistry === true) warnings.push({ source: "dnb", code: "query_incomplete", message: "A legal name or registration number with country is required for D&B search." });
  const responses = await Promise.allSettled(tasks);
  checkAborted(signal);
  const allExisting = [], registry = [];
  responses.forEach((result, index) => {
    const source = index === 0 ? "existing" : "dnb";
    try {
      if (result.status === "rejected") throw result.reason;
      const failure = responseError(result.value);
      if (failure) throw failure;
      const rows = responseRows(result.value, source);
      if (source === "existing") {
        existingSearchComplete = true;
        const seen = new Set();
        let rejectedScope = false;
        rows.forEach((row) => {
          // Do not attach a tenant to an unscoped returned record or trust a mismatched one.
          if (text(row?.tenantId) !== scope) { rejectedScope = true; return; }
          const companyId = text(row.companyId || row.id);
          if (!companyId || seen.has(companyId)) return;
          seen.add(companyId);
          const identity = entityIdentity(row);
          if (identity.name) allExisting.push({ ...identity, id: `existing:${companyId}`, companyId, tenantId: scope, source });
        });
        if (rejectedScope) {
          existingSearchComplete = false;
          warnings.push({ source, code: "tenant_scope_mismatch", message: "Some prospect results could not be verified as belonging to this tenant and were excluded. Retry before creating a prospect." });
        }
      } else {
        const seen = new Set();
        rows.forEach((entry) => {
          const identity = entityIdentity(entry?.organization || entry);
          if (!identity.name) return;
          // Exact repeated rows collapse; distinct entities sharing a name remain separate.
          const key = JSON.stringify([identity.dunsNumber, identity.registrationNumbers, identity.iso2Code, words(identity.name), words(identity.address)]);
          if (seen.has(key)) return;
          seen.add(key);
          registry.push({ ...identity, id: `dnb:${encodeURIComponent(key)}`, companyId: null, tenantId: scope, source });
        });
      }
    } catch (error) { warnings.push(searchWarning(source, error)); }
  });

  const relevantExisting = allExisting.filter((candidate) => compareIdentity(input, candidate).evidenceLevel !== "unrelated");
  const candidates = [...relevantExisting, ...registry].map((sourceCandidate) => {
    // Registry responses may list a tax number before the company registration.
    // Keep the full list, but display/submit the exact reviewed registration when
    // that identifier is present in the same country; do not substitute another ID.
    const reviewedRegistration = input.iso2Code && input.iso2Code === sourceCandidate.iso2Code
      ? sourceCandidate.registrationNumbers.find((value) => input.registrationNumbers.some((reviewed) => identifier(value) === identifier(reviewed)))
      : null;
    const candidate = reviewedRegistration ? { ...sourceCandidate, registrationNumber: reviewedRegistration } : sourceCandidate;
    const comparison = compareIdentity(input, candidate);
    if (candidate.source === "dnb" && registry.some((other) => other.id !== candidate.id &&
      ((candidate.dunsNumber && candidate.dunsNumber === other.dunsNumber) || sameRegistration(candidate, other)) &&
      compareIdentity(candidate, other).conflicts.length)) {
      comparison.conflicts.push("Registry results share an identifier but contain conflicting legal identifiers or country");
    }
    const existingMatches = allExisting.filter((other) => other.companyId === candidate.companyId ||
      (candidate.dunsNumber && candidate.dunsNumber === other.dunsNumber) || sameRegistration(candidate, other));
    const existingProspectIds = existingMatches.map((other) => other.companyId);
    if (existingMatches.some((other) => compareIdentity(candidate, other).conflicts.length)) {
      comparison.conflicts.push("An existing prospect shares an identifier but has conflicting legal identifiers or country");
    }
    const duplicateConflict = existingProspectIds.length > 1;
    const companyId = candidate.companyId || (existingProspectIds.length === 1 ? existingProspectIds[0] : null);
    const hasLegalIdentifier = Boolean(candidate.dunsNumber || (candidate.registrationNumber && candidate.iso2Code));
    const blockers = [];
    if (!hasLegalIdentifier) blockers.push("A DUNS number or registration number with country is required before matching");
    if (comparison.evidenceLevel === "unrelated") blockers.push("No shared legal name or identifier; refine the search");
    if (duplicateConflict) blockers.push("Multiple existing prospects share this identity; resolve the duplicate records before matching");
    if (!existingSearchComplete && !companyId) blockers.push("Existing prospect search must complete before creating a prospect");
    return {
      ...candidate, ...comparison, companyId, existingProspectIds, duplicateConflict, blockers,
      canMatch: !comparison.conflicts.length && !blockers.length, requiresReview: true,
    };
  });
  const rank = { identifier: 3, corroborated: 2, name_only: 1, unrelated: 0 };
  candidates.sort((left, right) => Number(Boolean(left.conflicts.length)) - Number(Boolean(right.conflicts.length)) || rank[right.evidenceLevel] - rank[left.evidenceLevel] || Number(right.source === "existing") - Number(left.source === "existing") || left.name.localeCompare(right.name) || left.id.localeCompare(right.id));
  const distinctRelated = new Set(candidates.filter((candidate) => candidate.evidenceLevel !== "unrelated").map((candidate) => candidate.companyId || (candidate.dunsNumber ? `duns:${candidate.dunsNumber}` : candidate.registrationNumber && candidate.iso2Code ? `reg:${candidate.iso2Code}:${identifier(candidate.registrationNumber)}` : candidate.id)));
  return {
    candidates, warnings, existingSearchComplete,
    ambiguous: distinctRelated.size > 1 || candidates.some((candidate) => candidate.duplicateConflict || candidate.conflicts.length || candidate.evidenceLevel === "name_only"),
    requiresSelection: true, selectedCandidateId: null,
  };
}
